import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import {
  channelKeyFromToken,
  isValidChannelSlug,
  type ChannelRow,
  type ChannelVisibility,
} from '../../lib/channels';

const inputClass =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500';

const VISIBILITIES: ChannelVisibility[] = ['public', 'private', 'internal'];

export default function ChannelModal({
  isOpen,
  onClose,
  channel,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  channel: ChannelRow | null;
  onSuccess: () => void;
}) {
  const [slug, setSlug] = useState('');
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [visibility, setVisibility] = useState<ChannelVisibility>('public');
  const [sortOrder, setSortOrder] = useState(0);
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (channel) {
      setSlug(channel.slug);
      setLabel(channel.label);
      setDescription(channel.description ?? '');
      setUrl(channel.url ?? '');
      setVisibility(channel.visibility);
      setSortOrder(channel.sort_order);
      setActive(channel.active);
    } else {
      setSlug('');
      setLabel('');
      setDescription('');
      setUrl('');
      setVisibility('public');
      setSortOrder(0);
      setActive(true);
    }
  }, [isOpen, channel]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const handleLabelBlur = () => {
    if (!channel && label.trim() && !slug.trim()) {
      setSlug(channelKeyFromToken(label));
    }
  };

  const handleSave = async () => {
    const normalizedSlug = channelKeyFromToken(slug || label);
    if (!isValidChannelSlug(normalizedSlug)) {
      toast.error('Slug must be 2–64 chars: lowercase letters, digits, underscores');
      return;
    }
    if (!label.trim()) {
      toast.error('Label is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        slug: normalizedSlug,
        label: label.trim(),
        description: description.trim() || null,
        url: url.trim() || null,
        visibility,
        sort_order: sortOrder,
        active,
      };

      if (channel) {
        const { error } = await supabase.from('channels').update(payload).eq('id', channel.id);
        if (error) throw error;
        toast.success('Channel updated');
      } else {
        const { error } = await supabase.from('channels').insert(payload);
        if (error) throw error;
        toast.success('Channel created');
      }
      onSuccess();
      onClose();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center p-0 sm:p-4 bg-black/50">
      <div
        className="bg-white dark:bg-gray-800 shadow-xl w-full max-w-lg flex flex-col min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {channel ? 'Edit channel' : 'New channel'}
          </h2>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1 min-h-0">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Label</label>
            <input
              className={inputClass}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onBlur={handleLabelBlur}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Slug</label>
            <input
              className={inputClass}
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              disabled={Boolean(channel)}
              placeholder="e.g. pickles"
            />
            {channel ? (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Slug cannot change after creation.</p>
            ) : null}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
            <textarea
              className={inputClass}
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Used in confirm and welcome emails when someone subscribes to this channel.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">URL</label>
            <input
              className={inputClass}
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://stephansmith.me/…"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Optional link in confirm/welcome email channel summaries. Leave blank for no link.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Visibility</label>
            <select
              className={inputClass}
              value={visibility}
              onChange={(e) => setVisibility(e.target.value as ChannelVisibility)}
            >
              {VISIBILITIES.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              public = signup + profile; private = admin assign + profile read-only; internal = engine only.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sort order</label>
              <input
                type="number"
                className={inputClass}
                value={sortOrder}
                onChange={(e) => setSortOrder(Number(e.target.value))}
              />
            </div>
            <div className="flex items-end pb-2">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
                Active
              </label>
            </div>
          </div>
        </div>

        <div className="shrink-0 flex justify-end gap-2 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
